import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { Certification } from 'src/types/Certification';
import { buildCertificationHubPath } from 'src/routes/renterPaths';

interface UseCertificationHubBackRouteArgs {
  certificationId?: number | null;
  surveyId?: number | null;
  /** Effective member user id for the hub path. Defaults to logged-in user. */
  userId?: number | null;
}

export interface CertificationHubBackRoute {
  backRoute: string | null;
  certification: Certification | null;
}

/**
 * Progress-gated back route to the certification hub (ENG-2968). Returns a
 * route only when the certification's questionnaire has been started —
 * renters with no progress never had a hub screen, so their back
 * affordances stay unchanged. Progress-based (not navigation-source based)
 * so deep links and refreshes behave identically.
 */
export function useCertificationHubBackRoute({
  certificationId,
  surveyId,
  userId,
}: UseCertificationHubBackRouteArgs): CertificationHubBackRoute {
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];
  const certificationsLoaded = useSelector(
    (state: RootState) => state.newRenterInterface.certificationsLoaded
  );

  return useMemo<CertificationHubBackRoute>(() => {
    const empty: CertificationHubBackRoute = {
      backRoute: null,
      certification: null,
    };
    if (!certificationsLoaded) {
      return empty;
    }
    const certList = Array.isArray(certifications) ? certifications : [];
    let cert: Certification | undefined;
    if (certificationId != null) {
      cert = certList.find((c) => c.id === Number(certificationId));
    } else if (surveyId != null) {
      cert = certList.find((c) =>
        c.cert_household?.cert_members?.some((m) =>
          (m.surveys ?? []).includes(Number(surveyId))
        )
      );
    }
    if (!cert || cert.questionnaire_started !== true) {
      return empty;
    }
    const effectiveUserId = userId ?? user?.id;
    if (effectiveUserId == null) {
      return { backRoute: null, certification: cert };
    }
    return {
      backRoute: buildCertificationHubPath(cert.id, effectiveUserId),
      certification: cert,
    };
  }, [
    certificationsLoaded,
    certifications,
    certificationId,
    surveyId,
    userId,
    user?.id,
  ]);
}
